import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { IDS } from '@/constants/testIds';
import { Loader2, ArrowRight, Camera, ShieldAlert, Wrench, AlertTriangle } from 'lucide-react';

export default function HistoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/problems');
        setItems(res.data);
      } catch (e) {
        console.error('Failed to load history:', e);
        setError(e.response?.data?.detail || 'Could not load your history. Try refreshing.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-24 flex justify-center">
        <Loader2 size={32} className="animate-spin text-amber-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="industrial-card border-t-4 border-t-red-500 text-center py-10">
          <AlertTriangle size={32} className="text-red-500 mx-auto mb-3" />
          <div className="font-display text-2xl text-zinc-100 tracking-wide">SOMETHING BROKE</div>
          <p className="text-zinc-400 text-sm mt-2">{error}</p>
          <Link to="/app" className="btn-primary mt-6 inline-flex text-sm"><Wrench size={14} /> Back to app</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4 fade-up">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-lime-400 mb-2">
            /history
          </div>
          <h1 className="font-display text-5xl sm:text-6xl text-zinc-100 tracking-tight leading-none">
            YOUR <span className="text-gradient-cool">WINS</span>
          </h1>
          <p className="text-zinc-400 mt-3">
            <span className="font-handwritten text-xl text-amber-400">Every fix you've conquered.</span> Keep it up.
          </p>
        </div>
        <Link to="/app" className="btn-primary hover-wiggle">
          <Wrench size={16} /> New Problem
        </Link>
      </div>

      {items.length === 0 ? (
        <div data-testid={IDS.historyEmpty} className="industrial-card text-center py-16 border-t-4 border-t-lime-400 bg-gradient-to-br from-lime-500/5 to-transparent">
          <div className="text-zinc-500 font-mono uppercase text-xs tracking-widest mb-3">
            /empty
          </div>
          <h2 className="font-display text-2xl text-zinc-200 mb-2">Nothing broken yet.</h2>
          <p className="text-zinc-500 mb-6">
            When you diagnose your first problem, it'll show up here.
          </p>
          <Link to="/app" className="btn-primary hover-wiggle">
            <Wrench size={16} /> Ask Your First Question
          </Link>
        </div>
      ) : (
        <div data-testid={IDS.historyList} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p, i) => (
            <Link
              key={p.id}
              data-testid={IDS.historyItem}
              to={`/solution/${p.id}`}
              className="industrial-card hover:border-amber-500 hover:-translate-y-1 hover:glow-amber transition-all block group glow-amber"
              style={{ animation: `fadeUp 0.5s ease-out ${i * 60}ms both` }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-amber-500">
                  {p.solution.trade}
                </div>
                <div className="flex items-center gap-2">
                  {p.has_image && <Camera size={12} className="text-zinc-500" />}
                  {p.solution.is_advanced && <ShieldAlert size={12} className="text-red-500" />}
                </div>
              </div>
              <div className="font-display text-xl text-zinc-100 leading-tight mb-2 line-clamp-2">
                {p.solution.title}
              </div>
              <div className="text-zinc-400 text-sm line-clamp-2 mb-4">{p.solution.summary}</div>
              <div className="flex items-center justify-between font-mono text-xs">
                <span className="text-zinc-500 uppercase">{p.solution.difficulty}</span>
                <span className="text-amber-500 group-hover:translate-x-1 transition-transform flex items-center gap-1">
                  Open <ArrowRight size={12} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
